import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { Button } from '../../../../ui/components/ui/button';
import Preloader from '../../../../ui/components/ui/preloader';
import { Error as ErrorI } from '../../../root/context';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import {
  CreateOfferRequestDto,
  OfferCompanyNameAvailabilityRequestDto,
  OfferMailAvailabilityRequestDto,
  SendOfferRequestDto
} from '../../types/request';
import { OfferAvailabilityResponseDto, OfferFormOptionsResponseDto, OfferMetaResponseDto, OfferResponseDto } from '../../types/response';
import OfferForm from './offer-form';
import { OfferFileChange, StoredOfferFile } from '../../lib/offer-file';
import { OfferFormValues, toOfferFormValues } from './offer-form.schema';

export interface OfferFormWrapperProps {
  billingTerms: BillingTermMetaResponseDto[];
  /** A draft to edit – without it a new offer is created. */
  offerId?: string;
  /** After saving – also when only the send failed, the draft is in the list then. */
  onSaved: () => void;
  onCancel: () => void;
  /** The offer is no draft (anymore) – it can only be looked at. */
  onShowDetail: (offerId: string) => void;
}

type LoadedDraft =
  | { status: 'ready'; initialValues?: OfferFormValues; storedOfferFile: StoredOfferFile | null }
  | { status: 'notDraft' }
  | { status: 'notFound' };

const OfferFormWrapper = ({ billingTerms, offerId, onSaved, onCancel, onShowDetail }: OfferFormWrapperProps) => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  // Loaded on every opening, so products, add-ons and start dates are current.
  const [options, setOptions] = useState<OfferFormOptionsResponseDto | null>(null);
  const [meta, setMeta] = useState<OfferMetaResponseDto | null>(null);
  const [draft, setDraft] = useState<LoadedDraft | null>(null);

  useEffect(() => {
    const fetchFormData = async () => {
      const [optionsRes, metaRes, offerRes] = await Promise.all([
        apiRequest<OfferFormOptionsResponseDto>('offers/admin/form-options', 'GET'),
        apiRequest<OfferMetaResponseDto>('offers/admin/meta', 'GET'),
        offerId ? apiRequest<OfferResponseDto>(`offers/admin/${offerId}`, 'GET') : Promise.resolve(null)
      ]);
      if (!optionsRes.data || !metaRes.data) return;
      setOptions(optionsRes.data);
      setMeta(metaRes.data);

      if (!offerRes) setDraft({ status: 'ready', storedOfferFile: null });
      else if (!offerRes.data) setDraft({ status: 'notFound' });
      else if (offerRes.data.sentAt) setDraft({ status: 'notDraft' });
      else {
        const { offerFile } = offerRes.data;
        setDraft({
          status: 'ready',
          initialValues: toOfferFormValues(offerRes.data, optionsRes.data, metaRes.data),
          storedOfferFile: offerFile && { name: offerFile.fileName, size: offerFile.file.size }
        });
      }
    };
    fetchFormData();
  }, [apiRequest, offerId]);

  // Stable per offer – the fields' debounced checks restart only when the value changes. The draft itself never conflicts.
  const checkMailAvailability = useCallback(
    async (mail: string): Promise<boolean | null> => {
      const body: OfferMailAvailabilityRequestDto = { mail, ...(offerId && { excludeOfferId: offerId }) };
      const res = await apiRequest<OfferAvailabilityResponseDto>('offers/admin/mail-validation', 'POST', { body });
      return res.data?.available ?? null;
    },
    [apiRequest, offerId]
  );

  const checkCompanyNameAvailability = useCallback(
    async (name: string): Promise<boolean | null> => {
      const body: OfferCompanyNameAvailabilityRequestDto = { name, ...(offerId && { excludeOfferId: offerId }) };
      const res = await apiRequest<OfferAvailabilityResponseDto>('offers/admin/company-name-validation', 'POST', { body });
      return res.data?.available ?? null;
    },
    [apiRequest, offerId]
  );

  // No `toast` option: a conflict is shown at its field, a toast would announce it a second time.
  const saveOffer = async (payload: CreateOfferRequestDto): Promise<{ offer: OfferResponseDto } | { error: ErrorI | null }> => {
    const res = offerId
      ? await apiRequest<OfferResponseDto>(`offers/admin/${offerId}`, 'PUT', { body: payload })
      : await apiRequest<OfferResponseDto>('offers/admin', 'POST', { body: payload });
    if (res.data) return { offer: res.data };
    // Without a response (network) there is neither an error nor the error modal.
    if (!res.error) toast.error('Es ist ein Fehler aufgetreten');
    return { error: (res.error as ErrorI | undefined) ?? null };
  };

  /** After the draft is saved – the file name goes as its own field (multer garbles umlauts in the multipart file name). */
  const applyOfferFileChange = async (savedOfferId: string, change: OfferFileChange): Promise<boolean> => {
    if (change.action === 'keep') return true;
    const path = `offers/admin/${savedOfferId}/offer-file`;
    const res =
      change.action === 'upload'
        ? await apiFileRequest<OfferResponseDto>(path, 'PUT', change.file, { formdataName: 'file', customFields: { fileName: change.file.name } })
        : await apiRequest<OfferResponseDto>(path, 'DELETE');
    return !!res.data;
  };

  const handleSaveDraft = async (payload: CreateOfferRequestDto, offerFile: OfferFileChange): Promise<ErrorI | null> => {
    const saved = await saveOffer(payload);
    if ('error' in saved) return saved.error;
    if (await applyOfferFileChange(saved.offer._id, offerFile)) toast.success('Entwurf wurde gespeichert.');
    else toast.error('Der Entwurf ist gespeichert, aber das Angebots-PDF nicht. Öffne ihn und versuche es erneut.');
    onSaved();
    return null;
  };

  const handleSend = async (payload: CreateOfferRequestDto, offerFile: OfferFileChange, validityDays: number): Promise<ErrorI | null> => {
    const saved = await saveOffer(payload);
    if ('error' in saved) return saved.error;
    // Never send without the PDF the admin picked – the draft stays and can be sent from the list.
    if (!(await applyOfferFileChange(saved.offer._id, offerFile))) {
      toast.error('Der Entwurf ist gespeichert, aber das Angebots-PDF nicht – deshalb wurde nichts versendet. Öffne ihn und versuche es erneut.');
      onSaved();
      return null;
    }

    const body: SendOfferRequestDto = { validityDays };
    const res = await apiRequest<OfferResponseDto>(`offers/admin/${saved.offer._id}/send`, 'POST', { body });
    if (res.data) toast.success('Angebot wurde versendet.');
    else toast.error('Der Entwurf ist gespeichert, aber der Versand hat nicht geklappt. Du kannst ihn in der Liste erneut starten.');
    onSaved();
    return null;
  };

  if (!options || !meta || !draft) return <Preloader />;

  if (draft.status !== 'ready') {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg bg-white px-9 py-7">
        <p className="text-[16px] text-[#12282A]">
          {draft.status === 'notFound'
            ? 'Dieses Angebot gibt es nicht (mehr).'
            : 'Dieses Angebot wurde schon versendet und kann nur noch angesehen werden.'}
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel}>
            Zur Übersicht
          </Button>
          {draft.status === 'notDraft' && offerId && <Button onClick={() => onShowDetail(offerId)}>Ansehen</Button>}
        </div>
      </div>
    );
  }

  return (
    <OfferForm
      billingTerms={billingTerms}
      meta={meta}
      options={options}
      initialValues={draft.initialValues}
      storedOfferFile={draft.storedOfferFile}
      onCancel={onCancel}
      onSaveDraft={handleSaveDraft}
      onSend={handleSend}
      checkMailAvailability={checkMailAvailability}
      checkCompanyNameAvailability={checkCompanyNameAvailability}
    />
  );
};

export default OfferFormWrapper;
